/**
 * confirm-dialog.js — GeoVoice-styled confirmation popup.
 *
 * Drop-in replacement for the native window.confirm():
 *
 *   import { confirmDialog } from '../utils/confirm-dialog.js';
 *   if (!(await confirmDialog({ message: 'Potje verwijderen?', danger: true }))) return;
 *
 * Renders a modal in the same look & feel as the rest of the app
 * (.modal-overlay / .modal-content pattern, --mv-* design tokens) and
 * resolves to true (confirmed) or false (cancelled / dismissed).
 */

import { LitElement, html, css } from 'lit';

export class ConfirmDialog extends LitElement {
  static properties = {
    titleText: { type: String },
    message: { type: String },
    confirmLabel: { type: String },
    cancelLabel: { type: String },
    danger: { type: Boolean },
    hideCancel: { type: Boolean },
    _open: { type: Boolean, state: true },
  };

  static styles = css`
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
    }
    .modal-overlay.open {
      opacity: 1;
      pointer-events: auto;
    }
    .modal-content {
      background: var(--mv-surface, #fff);
      border-radius: var(--mv-radius, 0.625rem);
      width: 90%;
      max-width: var(--dialog-max-width, 420px);
      box-shadow: var(--mv-shadow, 0 8px 24px rgba(0, 0, 0, 0.06));
      transform: translateY(20px) scale(0.95);
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      overflow: hidden;
    }
    .modal-overlay.open .modal-content {
      transform: translateY(0) scale(1);
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.25rem 1.5rem 0.75rem;
    }
    .modal-header h2 {
      margin: 0;
      font-size: 1.15rem;
      color: var(--mv-text, #181c32);
    }
    .close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      color: var(--mv-text-muted, #a1a5b7);
      cursor: pointer;
      padding: 0;
    }
    .modal-body {
      padding: 0 1.5rem 0.5rem;
      color: var(--mv-text-sub, #5e6278);
      font-size: 0.95rem;
      line-height: 1.5;
      white-space: pre-wrap;
    }
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 1.25rem 1.5rem;
    }
    button.action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem 1.1rem;
      border-radius: var(--mv-radius-sm, 0.375rem);
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;
    }
    .btn-cancel {
      background: var(--mv-surface-alt, #f8f9fa);
      color: var(--mv-text-sub, #5e6278);
      border-color: var(--mv-border, #eff2f5);
    }
    .btn-cancel:hover {
      background: var(--mv-border, #eff2f5);
    }
    .btn-confirm {
      background: var(--mv-primary, #4b9d35);
      color: #fff;
    }
    .btn-confirm:hover {
      background: var(--mv-primary-hover, #3d812b);
    }
    .btn-confirm.danger {
      background: var(--mv-danger, #f1416c);
    }
    .btn-confirm.danger:hover {
      filter: brightness(0.93);
    }
  `;

  constructor() {
    super();
    this.titleText = 'Bevestigen';
    this.message = '';
    this.confirmLabel = 'Bevestigen';
    this.cancelLabel = 'Annuleren';
    this.danger = false;
    this.hideCancel = false;
    this._open = false;
    this._resolve = null;
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  /** @returns {Promise<boolean>} */
  show() {
    document.addEventListener('keydown', this._onKeyDown);
    // Open on the next frame so the fade/scale transition plays.
    requestAnimationFrame(() => { this._open = true; });
    return new Promise((resolve) => { this._resolve = resolve; });
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this._close(false);
    else if (e.key === 'Enter') this._close(true);
  }

  _close(result) {
    if (!this._resolve) return;
    const resolve = this._resolve;
    this._resolve = null;
    document.removeEventListener('keydown', this._onKeyDown);
    this._open = false;
    // Wait for the close transition before removing from the DOM.
    setTimeout(() => {
      resolve(result);
      this.remove();
    }, 200);
  }

  render() {
    return html`
      <div
        class="modal-overlay ${this._open ? 'open' : ''}"
        @click=${(e) => { if (e.target === e.currentTarget) this._close(false); }}
      >
        <div class="modal-content" role="alertdialog" aria-modal="true">
          <div class="modal-header">
            <h2>${this.titleText}</h2>
            <button class="close-btn" @click=${() => this._close(false)} aria-label="Sluiten">&times;</button>
          </div>
          <div class="modal-body">${this.message}</div>
          <div class="modal-footer">
            ${this.hideCancel ? '' : html`
              <button class="action btn-cancel" @click=${() => this._close(false)}>
                ${this.cancelLabel}
              </button>
            `}
            <button
              class="action btn-confirm ${this.danger ? 'danger' : ''}"
              @click=${() => this._close(true)}
              autofocus
            >
              ${this.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('tv-confirm-dialog', ConfirmDialog);

/**
 * Show a GeoVoice-styled confirmation popup.
 *
 * @param {object} opts
 * @param {string} [opts.title]        Heading text.
 * @param {string} opts.message        Body question.
 * @param {string} [opts.confirmLabel] Confirm button label.
 * @param {string} [opts.cancelLabel]  Cancel button label.
 * @param {boolean} [opts.danger]      Style the confirm button as destructive (red).
 * @returns {Promise<boolean>} true when confirmed, false when cancelled/dismissed.
 */
export function confirmDialog({
  title = 'Bevestigen',
  message,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleren',
  danger = false,
} = {}) {
  const el = document.createElement('tv-confirm-dialog');
  el.titleText = title;
  el.message = message;
  el.confirmLabel = confirmLabel;
  el.cancelLabel = cancelLabel;
  el.danger = danger;
  document.body.appendChild(el);
  return el.show();
}

/**
 * Show a GeoVoice-styled info/alert popup (single OK button) — a styled drop-in
 * for the native window.alert().
 *
 * @param {object} opts
 * @param {string} [opts.title]   Heading text.
 * @param {string} opts.message   Body message.
 * @param {string} [opts.okLabel] OK button label.
 * @returns {Promise<void>}
 */
export function alertDialog({ title = 'Melding', message, okLabel = 'OK', maxWidth } = {}) {
  const el = document.createElement('tv-confirm-dialog');
  el.titleText = title;
  el.message = message;
  el.confirmLabel = okLabel;
  el.hideCancel = true;
  // Optioneel bredere dialog (alleen deze instance; default 420px blijft voor de rest).
  if (maxWidth) el.style.setProperty('--dialog-max-width', typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth);
  document.body.appendChild(el);
  return el.show().then(() => undefined);
}

/** GeoVoice-styled text-input popup — a styled drop-in for window.prompt(). */
export class PromptDialog extends LitElement {
  static properties = {
    titleText: { type: String },
    message: { type: String },
    placeholder: { type: String },
    confirmLabel: { type: String },
    cancelLabel: { type: String },
    _open: { type: Boolean, state: true },
    _value: { type: String, state: true },
    /** Meerregelig invoerveld i.p.v. één regel — voor notities en andere lopende tekst. */
    multiline: { type: Boolean },
    /** Label voor een verwijderknop links in de voettekst; leeg = geen knop. */
    deleteLabel: { type: String },
  };

  static styles = ConfirmDialog.styles;

  constructor() {
    super();
    this.titleText = 'Invoer';
    this.message = '';
    this.placeholder = '';
    this.confirmLabel = 'Opslaan';
    this.cancelLabel = 'Annuleren';
    this._open = false;
    this._value = '';
    this.multiline = false;
    this.deleteLabel = '';
    this._resolve = null;
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  /** @returns {Promise<string|null>} the entered text, or null when cancelled. */
  show() {
    document.addEventListener('keydown', this._onKeyDown);
    requestAnimationFrame(() => {
      this._open = true;
      this.updateComplete.then(() => { const i = this.shadowRoot?.querySelector('input'); if (i) { i.focus(); i.select(); } });
    });
    return new Promise((resolve) => { this._resolve = resolve; });
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') { this._close(null); return; }
    if (e.key !== 'Enter') return;
    // In een meerregelig veld hoort Enter een regel te maken, niet op te slaan. Daar is
    // Cmd/Ctrl+Enter de snelle weg naar opslaan; op één regel blijft Enter wat het was.
    if (this.multiline && !(e.metaKey || e.ctrlKey)) return;
    this._submit();
  }

  /**
   * Opslaan levert de TEKST op, ook als die leeg is. Annuleren levert null.
   *
   * Dat onderscheid is nodig: eerder gaven allebei null, waardoor de aanroeper niet kon zien
   * of iemand het veld had leeggemaakt of het venster had weggeklikt. Wie dat als "wissen"
   * las, vroeg bij elke sluitklik of er iets weg mocht.
   */
  _submit() {
    this._close((this._value || '').trim());
  }

  _close(result) {
    if (!this._resolve) return;
    const resolve = this._resolve;
    this._resolve = null;
    document.removeEventListener('keydown', this._onKeyDown);
    this._open = false;
    setTimeout(() => { resolve(result); this.remove(); }, 200);
  }

  render() {
    return html`
      <div class="modal-overlay ${this._open ? 'open' : ''}"
        @click=${(e) => { if (e.target === e.currentTarget) this._close(null); }}>
        <div class="modal-content" role="dialog" aria-modal="true">
          <div class="modal-header">
            <h2>${this.titleText}</h2>
            <button class="close-btn" @click=${() => this._close(null)} aria-label="Sluiten">&times;</button>
          </div>
          <div class="modal-body" style="white-space:normal;">${this.message ? html`<div style="margin-bottom:.5rem;">${this.message}</div>` : ''}${this.multiline
            ? html`<textarea rows="7" .value=${this._value} placeholder=${this.placeholder} @input=${(e) => this._value = e.target.value} style="width:100%;box-sizing:border-box;padding:.55rem .7rem;border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;font-size:.95rem;font-family:inherit;line-height:1.5;resize:vertical;color:var(--mv-text,#181c32);background:var(--mv-surface,#fff);"></textarea>`
            : html`<input type="text" .value=${this._value} placeholder=${this.placeholder} @input=${(e) => this._value = e.target.value} style="width:100%;box-sizing:border-box;padding:.55rem .7rem;border:1px solid var(--mv-border,#eff2f5);border-radius:.5rem;font-size:.95rem;font-family:inherit;color:var(--mv-text,#181c32);background:var(--mv-surface,#fff);">`}</div>
          <div class="modal-footer" style=${this.deleteLabel ? 'justify-content:space-between;' : ''}>
            ${this.deleteLabel ? html`
              <button class="action" style="border:none;background:none;color:var(--mv-danger,#f1416c);font-weight:600;padding:.5rem .25rem;cursor:pointer;"
                @click=${() => this._close('')}>${this.deleteLabel}</button>` : ''}
            <div style="display:flex;gap:.5rem;">
              <button class="action btn-cancel" @click=${() => this._close(null)}>${this.cancelLabel}</button>
              <button class="action btn-confirm" @click=${() => this._submit()}>${this.confirmLabel}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('tv-prompt-dialog', PromptDialog);

/**
 * Show a GeoVoice-styled prompt popup.
 * @returns {Promise<string|null>} the entered text, or null when cancelled/empty.
 */
export function promptDialog({ title = 'Invoer', message = '', placeholder = '', value = '', confirmLabel = 'Opslaan', cancelLabel = 'Annuleren', multiline = false, deleteLabel = '' } = {}) {
  const el = document.createElement('tv-prompt-dialog');
  el.titleText = title;
  el.message = message;
  el.placeholder = placeholder;
  el._value = value;
  el.confirmLabel = confirmLabel;
  el.cancelLabel = cancelLabel;
  el.multiline = multiline;
  el.deleteLabel = deleteLabel;
  document.body.appendChild(el);
  return el.show();
}
